export const firebaseErrorsHandler = (error: any) => {
  // Caso padrão do FirebaseError
  if (typeof error?.code === "string") {
    switch (error.code) {
      case "auth/user-not-found":
        return "Usuário não encontrado!";
      case "auth/wrong-password":
        return "A senha informada é inválida!";
      case "auth/user-disabled":
        return "Usuário desabilitado! Entre em contato com o administrador.";
      case "auth/invalid-credential":
        return "E-mail ou senha inválidos!";
      case "auth/invalid-login-credentials":
        return "E-mail ou senha inválidos!";
      case "auth/too-many-requests":
        return "Você errou seu login muitas vezes, tente novamente mais tarde!";
      default:
        break;
    }
  }

  // Novo padrão: erro aninhado vindo do backend REST do Firebase
  if (error?.message === "USER_DISABLED") {
    return "Usuário desabilitado! Entre em contato com o administrador.";
  }
  if (error?.message === "INVALID_PASSWORD") {
    return "A senha informada é inválida!";
  }
  if (error?.message === "EMAIL_NOT_FOUND") {
    return "Usuário não encontrado!";
  }
  if (
    error?.message === "INVALID_LOGIN_CREDENTIALS" ||
    error?.message === "INVALID_CREDENTIAL"
  ) {
    return "E-mail ou senha inválidos!";
  }

  // Se vier só message, tenta identificar pelo texto
  if (typeof error?.message === "string") {
    if (error.message.includes("USER_DISABLED")) {
      return "Usuário desabilitado! Entre em contato com o administrador.";
    }
    if (error.message.includes("INVALID_PASSWORD")) {
      return "A senha informada é inválida!";
    }
    if (error.message.includes("EMAIL_NOT_FOUND")) {
      return "Usuário não encontrado!";
    }
    if (
      error.message.includes("INVALID_LOGIN_CREDENTIALS") ||
      error.message.includes("INVALID_CREDENTIAL")
    ) {
      return "E-mail ou senha inválidos!";
    }
  }

  // Fallback
  return "Ocorreu um erro ao tentar processar seu login!";
};

export const formatCPF = (value: string) =>
  value
    .replace(/\D/g, "")
    .slice(0, 11)
    .replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");

export function CPFvalidator(cpf: string | undefined) {
  if (cpf === undefined) return false;
  cpf = cpf.replace(/[^\d]/g, "");
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) {
    return false;
  }
  let sum = 0;
  let rest;
  for (let i = 1; i <= 9; i++) {
    sum += parseInt(cpf[i - 1]) * (11 - i);
  }
  rest = (sum * 10) % 11;
  if (rest === 10 || rest === 11) {
    rest = 0;
  }
  if (rest !== parseInt(cpf[9])) {
    return false;
  }
  sum = 0;
  for (let i = 1; i <= 10; i++) {
    sum += parseInt(cpf[i - 1]) * (12 - i);
  }
  rest = (sum * 10) % 11;
  if (rest === 10 || rest === 11) {
    rest = 0;
  }
  if (rest !== parseInt(cpf[10])) {
    return false;
  }
  return true;
}

export const removerAcentos = (str: string) => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
};

export * from "./yup-validations";
export * from "./masks";
export * from "./appointmentUtils";
export * from "./passwordUtils";
export * from "./serviceUpsell";

/**
 * Verifica se um nome completo possui sobrenome válido (mínimo 3 caracteres)
 * @param fullName - Nome completo do cliente
 * @returns true se tiver sobrenome válido, false caso contrário
 */
export function hasValidSurname(fullName: string | undefined | null): boolean {
  if (!fullName || !fullName.trim()) {
    return false;
  }
  
  const nameParts = fullName.trim().split(/\s+/);
  
  // Se tiver menos de 2 partes (nome e sobrenome), não tem sobrenome válido
  if (nameParts.length < 2) {
    return false;
  }
  
  // Pega o último nome (sobrenome)
  const surname = nameParts[nameParts.length - 1];
  
  // Verifica se o sobrenome tem pelo menos 3 caracteres
  return surname.length >= 3;
}

// Gera os horários da agenda a partir do início, fim e intervalo
// Não inclui o último horário do expediente (end) para permitir agendamentos
export function generateTimeSlots(
  start: string,
  end: string,
  interval: number
) {
  const slots = [];
  let [h, m] = start.split(":").map(Number);
  const [endH, endM] = end.split(":").map(Number);

  // Converte o horário final para minutos para calcular o último slot válido
  const endMinutes = endH * 60 + endM;

  while (h < endH || (h === endH && m < endM)) {
    const currentMinutes = h * 60 + m;

    // Verifica se o slot atual é o último horário do expediente
    if (currentMinutes >= endMinutes) {
      break; // Para antes de incluir o último horário
    }

    const hour = String(h).padStart(2, "0");
    const min = String(m).padStart(2, "0");
    const slot = `${hour}:${min}`;
    slots.push(slot);

    m += interval;
    if (m >= 60) {
      h += Math.floor(m / 60);
      m = m % 60;
    }
  }

  return slots;
}

export const formatBirthDate = (birthDate: string): string => {
  if (birthDate.length === 10 && birthDate.includes("-")) {
    return birthDate.split("-").reverse().join("/");
  }
  return birthDate;
};

// Função para obter o agendamento de um dia específico
export function getDaySchedule(workingHours: any, dayOfWeek: number) {
  if (!workingHours) return null;

  const dayKey = dayOfWeek.toString() as keyof typeof workingHours;
  const daySchedule = workingHours[dayKey];

  if (
    daySchedule &&
    typeof daySchedule === "object" &&
    "active" in daySchedule &&
    "startTime" in daySchedule &&
    "endTime" in daySchedule &&
    "interval" in daySchedule
  ) {
    return daySchedule;
  }

  return null;
}

// Função para calcular o slot mais próximo baseado no timestamp e configurações de horário
export function calculateNearestTimeSlot(
  timestamp: number,
  workingHours: any
): string {
  const appointmentDate = new Date(timestamp);
  const dayOfWeek = appointmentDate.getDay();

  const daySchedule = getDaySchedule(workingHours, dayOfWeek);

  if (!daySchedule || !daySchedule.active) {
    return "Dia não disponível";
  }

  // Converte startTime para minutos desde o início do dia
  const [startHour, startMin] = daySchedule.startTime.split(":").map(Number);
  const startMinutes = startHour * 60 + startMin;

  // Converte endTime para minutos desde o início do dia
  const [endHour, endMin] = daySchedule.endTime.split(":").map(Number);
  const endMinutes = endHour * 60 + endMin;

  // Converte o horário do agendamento para minutos desde o início do dia
  const appointmentMinutes =
    appointmentDate.getHours() * 60 + appointmentDate.getMinutes();

  // Se o agendamento for antes do horário de abertura, usa o primeiro slot
  if (appointmentMinutes < startMinutes) {
    const hour = String(startHour).padStart(2, "0");
    const min = String(startMin).padStart(2, "0");
    const result = `${hour}:${min}`;
    return result;
  }

  // Se o agendamento for após o horário de fechamento, usa o último slot possível
  if (appointmentMinutes >= endMinutes) {
    // Calcula o último slot possível
    const totalSlots = Math.floor(
      (endMinutes - startMinutes) / daySchedule.interval
    );
    const lastSlotMinutes =
      startMinutes + (totalSlots - 1) * daySchedule.interval;
    const lastSlotHour = Math.floor(lastSlotMinutes / 60);
    const lastSlotMin = lastSlotMinutes % 60;
    const hour = String(lastSlotHour).padStart(2, "0");
    const min = String(lastSlotMin).padStart(2, "0");
    const result = `${hour}:${min}`;
    return result;
  }

  // Calcula qual slot é o mais próximo
  const minutesFromStart = appointmentMinutes - startMinutes;
  const slotIndex = Math.floor(minutesFromStart / daySchedule.interval);
  const nearestSlotMinutes = startMinutes + slotIndex * daySchedule.interval;

  const nearestSlotHour = Math.floor(nearestSlotMinutes / 60);
  const nearestSlotMin = nearestSlotMinutes % 60;

  const hour = String(nearestSlotHour).padStart(2, "0");
  const min = String(nearestSlotMin).padStart(2, "0");
  const result = `${hour}:${min}`;

  return result;
}

// Função para converter timestamp para formato de data e hora
export function timestampToDateTime(timestamp: number): {
  date: string;
  time: string;
} {
  const dateObj = new Date(timestamp);
  const date = getLocalDateString(dateObj); // YYYY-MM-DD no timezone local
  const time = dateObj.toTimeString().substring(0, 5); // HH:MM
  return { date, time };
}

// Função para converter data e hora para timestamp
export function dateTimeToTimestamp(date: string, time: string): number {
  // Cria a data no timezone local para evitar problemas de timezone
  const [year, month, day] = date.split("-").map(Number);
  const [hours, minutes] = time.split(":").map(Number);
  const result = new Date(year, month - 1, day, hours, minutes, 0).getTime();
  return result;
}

// Função para gerar horários de almoço dos profissionais para a agenda
export function generateLunchBreaks(
  professionals: any[],
  workingHours: any,
  selectedDate: string,
  customLunchs: any[] = []
): any[] {
  const lunchBreaks: any[] = [];

  // Obtém o dia da semana da data selecionada
  const selectedDateObj = parseLocalDateString(selectedDate);
  const dayOfWeek = selectedDateObj.getDay();
  const daySchedule = getDaySchedule(workingHours, dayOfWeek);

  if (!daySchedule || !daySchedule.active) {
    return lunchBreaks; // Retorna array vazio se o dia não estiver ativo
  }

  professionals.forEach((professional) => {
    // Verifica se existe um customLunch para este profissional neste dia
    const customLunch = customLunchs.find(
      (cl) => cl.professionalId === professional.id
    );

    if (customLunch) {
      if (customLunch.deleted) return; // Ignora almoços deletados

      // Usa o horário do customLunch
      const lunchTimestamp = dateTimeToTimestamp(
        selectedDate,
        customLunch.startTime
      );

      const nearestSlot = calculateNearestTimeSlot(
        lunchTimestamp,
        workingHours
      );

      if (nearestSlot !== "Dia não disponível") {
        // Calcula em quantos slots o almoço se encaixa baseado no início e fim
        const lunchStartMinutes = timeToMinutes(customLunch.startTime);
        const lunchEndMinutes = timeToMinutes(customLunch.endTime);
        const workingStartMinutes = timeToMinutes(daySchedule.startTime);

        // Calcula o índice do slot de início
        const startSlotIndex = Math.floor(
          (lunchStartMinutes - workingStartMinutes) / daySchedule.interval
        );

        // Calcula o índice do slot de fim (mesmo que ultrapasse um pouco)
        const endSlotIndex = Math.floor(
          (lunchEndMinutes - 1 - workingStartMinutes) / daySchedule.interval
        );

        // Quantidade de slots necessários
        const slotsNeeded = Math.max(1, endSlotIndex - startSlotIndex + 1);

        lunchBreaks.push({
          id: `custom_lunch_${customLunch.id}`,
          professionalId: professional.id,
          startTime: customLunch.startTime,
          endTime: customLunch.endTime,
          displayTime: nearestSlot,
          timestamp: lunchTimestamp,
          slotsNeeded: slotsNeeded,
          isLunch: true,
          professional: professional,
        });
      }
    } else if (professional.lunchStart && professional.lunchEnd) {
      // Usa o horário padrão do profissional se não houver customLunch
      const lunchTimestamp = dateTimeToTimestamp(
        selectedDate,
        professional.lunchStart
      );

      const nearestSlot = calculateNearestTimeSlot(
        lunchTimestamp,
        workingHours
      );

      if (nearestSlot !== "Dia não disponível") {
        // Calcula em quantos slots o almoço se encaixa baseado no início e fim
        const lunchStartMinutes = timeToMinutes(professional.lunchStart);
        const lunchEndMinutes = timeToMinutes(professional.lunchEnd);
        const workingStartMinutes = timeToMinutes(daySchedule.startTime);

        // Calcula o índice do slot de início
        const startSlotIndex = Math.floor(
          (lunchStartMinutes - workingStartMinutes) / daySchedule.interval
        );

        // Calcula o índice do slot de fim (mesmo que ultrapasse um pouco)
        const endSlotIndex = Math.floor(
          (lunchEndMinutes - 1 - workingStartMinutes) / daySchedule.interval
        );

        // Quantidade de slots necessários
        const slotsNeeded = Math.max(1, endSlotIndex - startSlotIndex + 1);

        lunchBreaks.push({
          id: `lunch_${professional.id}`,
          professionalId: professional.id,
          startTime: professional.lunchStart,
          endTime: professional.lunchEnd,
          displayTime: nearestSlot,
          timestamp: lunchTimestamp,
          slotsNeeded: slotsNeeded,
          isLunch: true,
          professional: professional,
        });
      }
    }
  });

  return lunchBreaks;
}

// Função auxiliar para converter horário HH:MM para minutos
export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(":").map(Number);
  return hours * 60 + minutes;
}

/**
 * Formata o preço para exibição em reais (BRL)
 * @param price - Valor em reais
 * @returns String formatada com símbolo da moeda
 */
export const formatPrice = (price: number): string => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(price);
};

/**
 * Formata a duração para exibição amigável
 * @param duration - Duração em minutos
 * @returns String formatada (ex: "30min", "1h", "1h 30min")
 */
export const formatDuration = (duration: number): string => {
  if (duration < 60) {
    return `${duration}min`;
  }
  const hours = Math.floor(duration / 60);
  const minutes = duration % 60;
  if (minutes === 0) {
    return `${hours}h`;
  }
  return `${hours}h ${minutes}min`;
};

/**
 * Gera uma lista de dias a partir de hoje até 30 dias à frente
 * @returns Array de objetos com data e informações do dia
 */
/**
 * Converte uma data para o timezone local em formato YYYY-MM-DD
 * @param date - Data a ser convertida
 * @returns String no formato YYYY-MM-DD no timezone local
 */
export function getLocalDateString(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Obtém a data atual no timezone local em formato YYYY-MM-DD
 * @returns String no formato YYYY-MM-DD no timezone local
 */
export function getTodayLocalDateString(): string {
  return getLocalDateString(new Date());
}

/**
 * Converte uma string de data YYYY-MM-DD para um objeto Date no timezone local
 * @param dateString - String no formato YYYY-MM-DD
 * @returns Objeto Date no timezone local
 */
export function parseLocalDateString(dateString: string): Date {
  const [year, month, day] = dateString.split("-").map(Number);
  return new Date(year, month - 1, day);
}

export function generateAvailableDays(): Array<{
  date: string; // YYYY-MM-DD
  dayName: string; // Abreviação do dia da semana
  dayNumber: number; // Número do dia
  month: string; // Abreviação do mês
}> {
  const days = [];
  const today = new Date();

  for (let i = 0; i < 30; i++) {
    const date = new Date(today);
    date.setDate(today.getDate() + i);

    // Usa timezone local para evitar problemas de timezone
    const dateString = getLocalDateString(date);
    const dayName = date
      .toLocaleDateString("pt-BR", { weekday: "short" })
      .replace(".", "");
    const dayNumber = date.getDate();
    const month = date
      .toLocaleDateString("pt-BR", { month: "short" })
      .replace(".", "");

    days.push({
      date: dateString,
      dayName,
      dayNumber,
      month,
    });
  }

  return days;
}

/**
 * Verifica se um horário inicial comporta um serviço de determinada duração,
 * usando uma lista de slots ocupados pré-calculada.
 */
function isSlotAvailableForDuration(
  startTime: string,
  serviceDuration: number,
  interval: number,
  timeSlots: string[],
  occupiedSlots: Set<string> | string[]
): boolean {
  const slotsNeeded = Math.ceil(serviceDuration / interval);

  // Encontra índice do slot inicial
  const startIndex = timeSlots.indexOf(startTime);
  if (startIndex === -1) return false;

  // Verifica se tem slots suficientes até o final do expediente
  const requiredSlots = timeSlots.slice(startIndex, startIndex + slotsNeeded);
  if (requiredSlots.length < slotsNeeded) return false;

  // Checa se algum desses slots está ocupado
  for (const slot of requiredSlots) {
    if (Array.isArray(occupiedSlots)) {
      if (occupiedSlots.includes(slot)) return false;
    } else {
      if (occupiedSlots.has(slot)) return false;
    }
  }

  return true;
}

/** Antecedência mínima (em minutos) para agendar no mesmo dia */
export const MINIMUM_BOOKING_ADVANCE_MINUTES = 20;

/**
 * Gera horários disponíveis para um profissional em uma data específica
 * @param professional - Profissional
 * @param date - Data no formato YYYY-MM-DD
 * @param workingHours - Configurações de horário de funcionamento
 * @param appointments - Agendamentos existentes para a data
 * @param commitments - Compromissos existentes para a data
 * @param serviceDuration - Duração de um serviço para verificar disponibilidade
 * @returns Array de horários com flag de disponibilidade
 */
export function generateAvailableTimeSlots(
  professional: any,
  date: string,
  workingHours: any,
  appointments: any[] = [],
  commitments: any[] = [],
  fixedCommitments: any[] = [],
  customLunchs: any[] = [],
  serviceDuration?: number
): Array<{
  time: string; // HH:MM
  available: boolean;
  appointment?: any; // Agendamento que ocupa o horário
  commitment?: any; // Compromisso que ocupa o horário
  isLunch?: boolean; // Se é horário de almoço
}> {
  if (!workingHours) return [];

  // Verifica se o dia da semana está nos dias de funcionamento
  const selectedDate = parseLocalDateString(date);
  const dayOfWeek = selectedDate.getDay();

  const daySchedule = getDaySchedule(workingHours, dayOfWeek);

  if (!daySchedule || !daySchedule.active) {
    return [];
  }

  // Gera todos os slots de horário para o dia
  const timeSlots = generateTimeSlots(
    daySchedule.startTime,
    daySchedule.endTime,
    daySchedule.interval
  );

  // Filtra agendamentos e compromissos do profissional para a data
  const professionalAppointments = appointments.filter(
    (app) =>
      app.professionalId === professional.id &&
      getLocalDateString(new Date(app.timestamp)) === date
  );

  const professionalCommitments = commitments.filter(
    (commit) =>
      (commit.professionalId === professional.id ||
        (commit.isForAll && commit.professionalId === "all")) &&
      getLocalDateString(new Date(commit.timestamp || 0)) === date
  );

  // Processa fixedCommitments para a data específica
  const dayOfMonth = selectedDate.getDate();
  const virtualCommitments: any[] = [];

  fixedCommitments.forEach((fixedCommitment) => {
    let shouldShow = false;

    // Verifica se deve mostrar baseado no tipo de recorrência
    if (
      fixedCommitment.recurrenceType === "weekly" &&
      fixedCommitment.weeklyDays
    ) {
      shouldShow = fixedCommitment.weeklyDays.includes(dayOfWeek);
    } else if (
      fixedCommitment.recurrenceType === "monthly" &&
      fixedCommitment.monthlyDay
    ) {
      shouldShow = fixedCommitment.monthlyDay === dayOfMonth;
    }

    // Verifica se há exceções (datas a serem ignoradas)
    // exceptions deve ser um array de strings no formato "YYYY-MM-DD"
    const isException =
      Array.isArray(fixedCommitment.exceptions) &&
      fixedCommitment.exceptions.includes(date);
    // Verifica se é para este profissional ou para todos
    const isForThisProfessional =
      fixedCommitment.professionalId === professional.id ||
      (fixedCommitment.isForAll && fixedCommitment.professionalId === "all");

    // Só cria o compromisso virtual se:
    //  - ele é recorrente neste dia (shouldShow)
    //  - pertence ao profissional atual
    //  - a data não está dentro das exceções
    if (shouldShow && isForThisProfessional && !isException) {
      const virtualCommitment = {
        ...fixedCommitment,
        id: `virtual_${fixedCommitment.id}_${date}`,
        timestamp: new Date(
          `${date}T${fixedCommitment.startTime}:00`
        ).getTime(),
      };
      virtualCommitments.push(virtualCommitment);
    }
  });

  // Combina compromissos normais com compromissos virtuais
  const allCommitments = [...professionalCommitments, ...virtualCommitments];

  // Gera horários de almoço do profissional
  const lunchBreaks = generateLunchBreaks(
    [professional],
    workingHours,
    date,
    customLunchs
  );

  // Cria um mapa de slots ocupados baseado na duração dos agendamentos e compromissos
  const occupiedSlots = new Set<string>();

  // Processa agendamentos - calcula todos os slots que cada agendamento ocupa
  professionalAppointments.forEach((appointment) => {
    const nearestSlot = calculateNearestTimeSlot(
      appointment.timestamp,
      workingHours
    );

    if (nearestSlot === "Dia não disponível") return;

    const slotsNeeded = Math.ceil(
      appointment.service.duration / daySchedule.interval
    );

    const startIndex = timeSlots.findIndex((slot) => slot === nearestSlot);

    if (startIndex !== -1) {
      // Marca todos os slots que o agendamento ocupa
      for (let i = 0; i < slotsNeeded; i++) {
        const slotIndex = startIndex + i;
        if (slotIndex < timeSlots.length) {
          occupiedSlots.add(timeSlots[slotIndex]);
        }
      }
    }
  });

  // Processa compromissos - calcula todos os slots que cada compromisso ocupa
  allCommitments.forEach((commitment) => {
    if (commitment.allDay) {
      // Para compromissos de dia todo, ocupa TODOS os slots disponíveis do dia
      timeSlots.forEach((slot) => {
        occupiedSlots.add(slot);
      });
    } else {
      // Para compromissos normais, usa a lógica de timestamp
      const nearestSlot = calculateNearestTimeSlot(
        commitment.timestamp || 0,
        workingHours
      );

      if (nearestSlot === "Dia não disponível") return;

      const slotsNeeded: number = Math.ceil(commitment.duration / daySchedule.interval);
      const startIndex = timeSlots.findIndex((slot) => slot === nearestSlot);
      
      if (startIndex !== -1) {
        // Marca todos os slots que o compromisso ocupa
        for (let i = 0; i < slotsNeeded; i++) {
          const slotIndex = startIndex + i;
          if (slotIndex < timeSlots.length) {
            occupiedSlots.add(timeSlots[slotIndex]);
          }
        }
      }
    }
  });

  // Verifica se é o dia atual para filtrar horários passados
  const today = getTodayLocalDateString();
  const isToday = date === today;
  const currentTime = new Date();
  const currentMinutes = currentTime.getHours() * 60 + currentTime.getMinutes();

  // Mapeia os slots para verificar disponibilidade
  return timeSlots.map((timeSlot) => {
    // Se é o dia atual, verifica se o horário já passou e se respeita o tempo de antecedência
    if (isToday) {
      const slotMinutes = timeToMinutes(timeSlot);

      // Verifica se o horário já passou
      if (slotMinutes <= currentMinutes) {
        return {
          time: timeSlot,
          available: false,
        };
      }

      // Verifica se respeita o tempo de antecedência mínima
      const timeDifference = slotMinutes - currentMinutes;

      if (timeDifference < MINIMUM_BOOKING_ADVANCE_MINUTES) {
        return {
          time: timeSlot,
          available: false,
        };
      }
    }

    // Verifica se é horário de almoço
    const isLunchTime = lunchBreaks.some((lunch) => {
      const lunchStartMinutes = timeToMinutes(lunch.startTime);
      const lunchEndMinutes = timeToMinutes(lunch.endTime);
      const slotMinutes = timeToMinutes(timeSlot);
      return slotMinutes >= lunchStartMinutes && slotMinutes < lunchEndMinutes;
    });

    if (isLunchTime) {
      return {
        time: timeSlot,
        available: false,
        isLunch: true,
      };
    }

    // Verifica se o slot está ocupado por agendamento ou compromisso
    if (occupiedSlots.has(timeSlot)) {
      // Encontra qual agendamento ou compromisso ocupa este slot
      const conflictingAppointment = professionalAppointments.find((app) => {
        const nearestSlot = calculateNearestTimeSlot(
          app.timestamp,
          workingHours
        );
        if (nearestSlot === "Dia não disponível") return false;

        const slotsNeeded = Math.ceil(
          app.service.duration / daySchedule.interval
        );
        const startIndex = timeSlots.findIndex((slot) => slot === nearestSlot);

        if (startIndex !== -1) {
          for (let i = 0; i < slotsNeeded; i++) {
            const slotIndex = startIndex + i;
            if (
              slotIndex < timeSlots.length &&
              timeSlots[slotIndex] === timeSlot
            ) {
              return true;
            }
          }
        }
        return false;
      });

      const conflictingCommitment = allCommitments.find((commit) => {
        const nearestSlot = calculateNearestTimeSlot(
          commit.timestamp || 0,
          workingHours
        );
        if (nearestSlot === "Dia não disponível") return false;

        let slotsNeeded: number;
        if (commit.allDay) {
          slotsNeeded = timeSlots.length;
        } else {
         
            slotsNeeded = Math.ceil(commit.duration / daySchedule.interval);
        }

        const startIndex = timeSlots.findIndex((slot) => slot === nearestSlot);

        if (startIndex !== -1) {
          for (let i = 0; i < slotsNeeded; i++) {
            const slotIndex = startIndex + i;
            if (
              slotIndex < timeSlots.length &&
              timeSlots[slotIndex] === timeSlot
            ) {
              return true;
            }
          }
        }
        return false;
      });

      return {
        time: timeSlot,
        available: false,
        appointment: conflictingAppointment,
        commitment: conflictingCommitment,
      };
    }

    if (serviceDuration) {
      const available = isSlotAvailableForDuration(
        timeSlot,
        serviceDuration,
        daySchedule.interval,
        timeSlots,
        occupiedSlots
      );
      if (!available)
        return {
          time: timeSlot,
          available: false,
        };
    }

    // Horário disponível
    return {
      time: timeSlot,
      available: true,
    };
  });
}

/**
 * Gera horários disponíveis para todos os profissionais em uma data específica
 * @param professionals - Lista de profissionais
 * @param date - Data no formato YYYY-MM-DD
 * @param workingHours - Configurações de horário de funcionamento
 * @param appointments - Agendamentos existentes para a data
 * @param commitments - Compromissos existentes para a data
 * @param serviceDuration - Duração de um serviço para verificar disponibilidade
 * @returns Objeto com horários por profissional
 */
export function generateAllProfessionalsTimeSlots(
  professionals: any[],
  date: string,
  workingHours: any,
  appointments: any[] = [],
  commitments: any[] = [],
  fixedCommitments: any[] = [],
  customLunchs: any[] = [],
  serviceDuration?: number
): Record<
  string,
  Array<{
    time: string;
    available: boolean;
    appointment?: any;
    commitment?: any;
    isLunch?: boolean;
  }>
> {
  const result: Record<string, any[]> = {};

  professionals.forEach((professional) => {
    if (professional.enabled) {
      result[professional.id] = generateAvailableTimeSlots(
        professional,
        date,
        workingHours,
        appointments,
        commitments,
        fixedCommitments,
        customLunchs,
        serviceDuration
      );
    }
  });

  return result;
}
